import * as Sentry from '@sentry/react';

/**
 * Matches the suffix `generateErrorId()` appends to a thrown message.
 *
 * The id stays in the message on purpose — it is what the error screen shows
 * the user to quote to support. It must NOT reach Sentry's grouping, though:
 * a fresh uuid per throw is what split one failure into four issues
 * (PRO-STUDIO-41, 5T, 4H, 4Z), each titled with the id of whichever event
 * happened to open it.
 */
const ERROR_ID_SUFFIX =
  /\s*\(Error ID:\s*([0-9a-fA-F-]+)\s*please pass this ID to the support service\)\s*$/;

export interface SplitMessage {
  /** The message with the support-id suffix removed. */
  message: string;
  /** The uuid that suffix carried, when it had one. */
  errorId?: string;
}

export const splitErrorId = (raw: string): SplitMessage => {
  const match = raw.match(ERROR_ID_SUFFIX);

  if (!match) {
    return { message: raw, errorId: undefined };
  }

  return { message: raw.replace(ERROR_ID_SUFFIX, ''), errorId: match[1] };
};

export interface ReportErrorOptions {
  /** Where the report was raised — an error boundary, a machine, a hook. */
  source: string;
  /** A coarse kind, kept from the previous `extra.type`. */
  type?: string;
  tags?: Record<string, string>;
  /** Anything genuinely non-indexable. Platform identity belongs in tags. */
  extra?: Record<string, unknown>;
}

/**
 * The one place this monorepo hands an error to Sentry.
 *
 * Two things it does that a bare `captureException` does not: it groups by the
 * message with the per-throw support id stripped out, and it puts that id on a
 * searchable tag instead. The minified `stack` the previous call sites also
 * copied into `extra` is deliberately not carried — Sentry already resolves the
 * real one through the uploaded sourcemaps.
 */
export const reportError = (
  error: unknown,
  { source, type, tags, extra }: ReportErrorOptions
) => {
  const raw = error instanceof Error ? error.message : String(error);
  const { message, errorId } = splitErrorId(raw);
  const name = error instanceof Error ? error.name : 'UnknownError';

  Sentry.captureException(error, {
    // Only override grouping where it is actually broken — i.e. where a unique
    // support id sits in the message. Setting it unconditionally would replace
    // Sentry's stack-aware grouping everywhere, merging unrelated failures that
    // happen to share a message; for a non-Error value `String(error)` is
    // "[object Object]", which would collapse every such report in the monorepo
    // into one issue.
    ...(errorId ? { fingerprint: [name, message] } : {}),
    tags: {
      ...tags,
      ...(errorId ? { errorId } : {}),
    },
    extra: {
      source,
      ...(type ? { type } : {}),
      ...extra,
    },
  });
};

export interface UserIdentity {
  id?: string;
  email?: string;
  /** Account-level identity — searchable, so these become TAGS, not extra. */
  customerId?: string;
  orgName?: string;
  projectId?: string;
  solutionName?: string;
}

const ACCOUNT_TAG_KEYS = [
  'customerId',
  'orgName',
  'projectId',
  'solutionName',
] as const;

/**
 * Attaches who is affected, and which account they are in, to every later event.
 *
 * `null` clears BOTH the user and the account tags. `Sentry.setUser(null)` alone
 * would not: `setTags` merges into the scope (`@sentry/core` `scope.js`), so a
 * tag stays until something overwrites it. On sign-out that would credit the
 * next person's errors to the previous account.
 *
 * The same merge is why a missing value is written as `undefined` rather than
 * skipped. Leaving a key out keeps the PREVIOUS value: navigating from a
 * solution back to the project list clears `solutionName`, and every later event
 * would still carry the solution the user has left. A key set to `undefined`
 * drops out when the event is serialized, so it neither lingers nor arrives as
 * the literal string "undefined".
 */
export const identifyUser = (identity: UserIdentity | null) => {
  const tags = Object.fromEntries(
    ACCOUNT_TAG_KEYS.map((key) => [key, identity?.[key] || undefined])
  );

  if (!identity) {
    Sentry.setUser(null);
    Sentry.setTags(tags);

    return;
  }

  Sentry.setUser({ id: identity.id, email: identity.email });
  Sentry.setTags(tags);
};
